/**
 * App-wide RBAC notices, shown by RbacNoticeHost (rendered next to <App/> in
 * main.ts, so a notice also shows on full-page routes and survives the move
 * to the sign-in page):
 *  - sessionRevoked — an administrator ended your session;
 *  - noAccessToItem — a link (notification, card) points somewhere you can't open.
 * A permission change that closes the open page is silent (owner decision:
 * employees are not told their access changed).
 */
import { shallowRef } from 'vue';

export type RbacNoticeKind = 'sessionRevoked' | 'noAccessToItem';

export interface RbacNotice {
  kind: RbacNoticeKind;
  /** Increments on every show, so the same notice twice restarts its timer. */
  id: number;
}

export const NOTICE_MESSAGE_KEYS: Readonly<Record<RbacNoticeKind, string>> = {
  sessionRevoked: 'rbac.sessionRevoked',
  noAccessToItem: 'rbac.noAccessToItem',
};

/**
 * Auto-hide delay per kind (ms); null = stays until dismissed. Every notice
 * also ends with the session: stores/sessionScope.ts clears it on sign-in
 * and sign-out (a "session revoked" notice shown right after the local
 * sign-out stays on the sign-in page until it times out or someone signs in).
 */
export const NOTICE_AUTO_HIDE_MS: Readonly<Record<RbacNoticeKind, number | null>> = {
  sessionRevoked: 10000,
  noAccessToItem: 6000,
};

export const rbacNotice = shallowRef<RbacNotice | null>(null);

let nextId = 1;

export function showRbacNotice(kind: RbacNoticeKind): void {
  rbacNotice.value = { kind, id: nextId++ };
}

export function dismissRbacNotice(): void {
  rbacNotice.value = null;
}
